import { useRef, type CSSProperties, type ReactNode } from "react";
import {
  selectedOptionBadge,
  type CreativeOptionKind,
  type StoreCategoryId,
} from "../../game/store-catalog.js";
import { useStudioStore } from "./StudioStoreContext.js";

/**
 * Creative option card. Tool panels now render only OWNED options (plus the
 * currently applied one), so locked cards no longer clutter the panel and there
 * is no per-card "Get in Store" button — new options are bought via the panel's
 * cart, which opens the Studio Bag store scrolled to that tool's category.
 *
 * The lock badge only surfaces in the edge case where a loaded graph still
 * carries an unowned option that is kept visible so it can be seen / cleared.
 */
export function CreativeOptionCard(props: {
  kind: CreativeOptionKind;
  optionId: string;
  label: string;
  testId: string;
  selected: boolean;
  disabled?: boolean;
  title?: string;
  preview?: ReactNode;
  /** Optional content rendered under the label (e.g. impact chips). */
  footer?: ReactNode;
  className?: string;
  onApply: () => void;
  onPreview?: () => void;
}) {
  const store = useStudioStore();
  const cardBtnRef = useRef<HTMLButtonElement>(null);
  const owned = store.isOptionOwned(props.kind, props.optionId);
  const locked = !owned;
  const badge = selectedOptionBadge({
    owned,
    inChallenge: store.inChallenge,
  });
  const showSelectedBadge = props.selected && badge;

  const activate = () => {
    if (locked) {
      props.onPreview?.();
      return;
    }
    props.onApply();
  };

  return (
    <div
      className={`creative-option-wrap${locked ? " is-locked" : ""}${props.selected ? " is-selected" : ""}`}
      data-testid={`${props.testId}-wrap`}
      data-owned={owned ? "1" : "0"}
    >
      <button
        ref={cardBtnRef}
        type="button"
        className={`treatment treatment-card${props.className ? ` ${props.className}` : ""}${props.selected ? " active" : ""}${locked ? " locked" : ""}`}
        data-testid={props.testId}
        aria-pressed={props.selected}
        aria-label={
          locked ? `${props.label}, locked, preview only` : props.label
        }
        disabled={props.disabled}
        title={
          props.title ??
          (locked ? "Preview only, get it in the Studio Bag" : undefined)
        }
        onClick={activate}
      >
        {locked ? (
          <span className="lock-badge" aria-hidden data-testid={`${props.testId}-lock`}>
            Locked
          </span>
        ) : null}
        {props.preview}
        <strong>{props.label}</strong>
        {showSelectedBadge ? (
          <span className="option-status-badge" data-testid={`${props.testId}-status`}>
            {badge}
          </span>
        ) : null}
        {props.footer}
      </button>
    </div>
  );
}

export function kindToStoreCategory(kind: CreativeOptionKind): StoreCategoryId {
  switch (kind) {
    case "headline":
      return "headline-style";
    case "sfx":
      return "sfx";
    case "look":
      return "looks";
    case "motion":
      return "motion";
    case "sticker":
    case "transition":
      return "decorate";
    default:
      return "utility";
  }
}

export function lookPreviewStyle(t: {
  contrastMilli: number;
  saturationMilli: number;
  warmthMilli: number;
  vignetteMilli: number;
}): CSSProperties {
  const contrast = t.contrastMilli / 1000;
  const sat = t.saturationMilli / 1000;
  const warmth = (t.warmthMilli - 1000) / 1000;
  const hue = warmth * 18;
  const sepia = Math.max(0, warmth) * 0.35;
  const bright = 1 - t.vignetteMilli / 4000;
  return {
    filter: `contrast(${contrast}) saturate(${sat}) hue-rotate(${hue}deg) sepia(${sepia}) brightness(${bright})`,
  };
}
